/**
 * ThreadTabBar - Tab bar for thread navigation (Windsurf-style)
 *
 * Shows:
 * 1. Current thread tab (active, with title)
 * 2. "+" button to start a new chat
 * 3. Dropdown with search + full thread list ("All Conversations")
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Popover,
  ButtonBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  Divider,
  useMediaQuery,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import BookmarkBorderRounded from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRounded from '@mui/icons-material/BookmarkRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import SaveToSpaceModal from '../../../components/SaveToSpaceModal';
import SaveToPlaybookModal from '../../../components/SaveToPlaybookModal';
import { listSpaces, createSpace } from '../../../services/spacesService';
import type { Space } from '../../../services/spacesService';
import { buildApiUrl, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { SnackbarContext } from '../../../ui/SnackbarProvider';

import { useCopilotStore } from '../stores/useCopilotStore';
import type { CopilotThread } from '../stores/useCopilotStore';
import { useThreadListLoader } from '../hooks/useCopilotHistory';
import { useCopilotThreadActions } from '../hooks/useCopilotThreadActions';
import { ConversationList, ToolbarButton, useDebouncedCommit } from '../../../ui-kit';

interface ThreadTabBarProps {
  threadTitle: string | null;
  isStreaming: boolean;
  /** Tell the page which thread was picked, instead of opening /copilot/<id>. */
  onOpenThread?: (threadId: string, title: string | null) => void;
  /** Tell the page a new chat was asked for, instead of opening /copilot/<new id>. */
  onNewChat?: () => void;
  /**
   * `tab` — the title styled as a tab (the current panel).
   * `plain` — the title as a heading with ▾ to switch threads, New chat as an
   * icon; for the new panel, where there is only ever one thread showing.
   */
  variant?: 'tab' | 'plain';
  /**
   * Offer the dropdown of conversations and New chat here. Turn off where the
   * page already lists conversations beside the chat.
   */
  showSwitcher?: boolean;
}

export default function ThreadTabBar({
  threadTitle,
  isStreaming,
  onOpenThread,
  onNewChat,
  variant = 'tab',
  showSwitcher = true,
}: ThreadTabBarProps) {
  const plain = variant === 'plain';
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const { openThread, newChat } = useCopilotThreadActions();
  const { notify } = React.useContext(SnackbarContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  // The list is searched once typing pauses; the box shows every keystroke.
  const [query, setQuery] = useState('');
  const [search, setSearch] = useDebouncedCommit(query, setQuery, 300);

  const {
    threads,
    hasMore,
    loading: threadsLoading,
    loadingMore: threadsLoadingMore,
    loadThreads,
  } = useThreadListLoader();
  const currentThreadId = useCopilotStore((s: any) => s.threadId) as string | null;

  // Save to Playbook state
  const [playbookModalOpen, setPlaybookModalOpen] = useState(false);

  // Save to Space state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Check if current thread is already saved whenever threadId changes
  React.useEffect(() => {
    setIsSaved(false);
    if (!currentThreadId) return;
    const token = localStorage.getItem('klaris_jwt');
    const tid = getTenantIdFromStorage();
    if (!tid) return;
    fetch(
      buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${currentThreadId}/saved`),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.saved) setIsSaved(true); })
      .catch(() => {});
  }, [currentThreadId]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    void loadThreads({ offset: 0, replace: true, q: query.trim() });
  }, [open, query, loadThreads]);

  const handleOpenDropdown = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setQuery('');
  };

  const handleNewChat = () => {
    setAnchorEl(null);
    if (onNewChat) {
      onNewChat();
      return;
    }
    newChat();
  };

  const handleSelectThread = (threadId: string, title: string | null) => {
    if (threadId !== currentThreadId) {
      if (onOpenThread) onOpenThread(threadId, title);
      else openThread(threadId, title);
    }
    setAnchorEl(null);
  };

  const handleOpenSaveModal = async () => {
    setSaveModalOpen(true);
    setSpacesLoading(true);
    setSpacesError(null);
    setSelectedSpaceId(null);
    setNewSpaceName('');
    setSubmitError(null);
    try {
      const loaded = await listSpaces();
      setSpaces(loaded);
    } catch (err: any) {
      setSpacesError(err.message || 'Failed to load spaces');
    } finally {
      setSpacesLoading(false);
    }
  };

  const handleSaveToSpace = async () => {
    if (!currentThreadId) return;
    if (!selectedSpaceId && !newSpaceName.trim()) {
      setSubmitError('Please select a category or create a new one');
      return;
    }
    setIsSaving(true);
    setSubmitError(null);
    try {
      let spaceId = selectedSpaceId;
      if (!spaceId && newSpaceName.trim()) {
        const newSpace = await createSpace(newSpaceName.trim());
        spaceId = newSpace.space_id;
      }
      if (!spaceId) throw new Error('No space selected');
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/copilot/threads/${currentThreadId}/save`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ space_id: spaceId }),
        },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      const result = await resp.json();
      setIsSaved(true);
      setSaveModalOpen(false);
      notify(result.already_exists ? 'Already saved in this space' : 'Saved to space!', result.already_exists ? 'info' : 'success');
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Tab bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 38,
          px: 0.5,
          gap: 0,
          bgcolor: 'transparent',
          overflowX: 'auto',
          overflowY: 'hidden',
          flexShrink: 0,
          '&::-webkit-scrollbar': { height: 4 },
        }}
      >
        {/* Active tab */}
        <ButtonBase
          onClick={showSwitcher ? handleOpenDropdown : undefined}
          disabled={!showSwitcher}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            // Plain: a heading, not a tab — no box, no underline, and room to
            // show the whole title. Tab: the current panel's look.
            borderRadius: plain ? `${theme.ds.radius.sm}px` : '6px 6px 0 0',
            maxWidth: plain ? '100%' : isNarrow ? 160 : 260,
            minWidth: 0,
            flexShrink: 1,
            bgcolor: plain ? 'transparent' : isDark ? 'background.default' : '#fff',
            borderBottom: plain ? 'none' : `2px solid ${theme.palette.primary.main}`,
            '&:hover': {
              bgcolor: plain && !showSwitcher ? 'transparent' : isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02),
            },
            '&.Mui-disabled': { color: 'inherit' },
          }}
        >
          {isStreaming ? (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'success.main',
                flexShrink: 0,
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
          ) : plain ? null : (
            <ChatBubbleOutlineRounded
              sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant={plain ? 'subtitle2' : 'caption'}
              noWrap
              title={threadTitle || 'Untitled chat'}
              sx={plain ? { display: 'block', color: 'text.primary' } : {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.primary',
                display: 'block',
              }}
            >
              {threadTitle || 'Untitled chat'}
            </Typography>
          </Box>
          {showSwitcher && (
            <ExpandMoreRounded
              sx={{
                fontSize: 15,
                color: 'text.secondary',
                flexShrink: 0,
                transition: 'transform 0.2s',
                transform: open ? 'rotate(180deg)' : 'none',
              }}
            />
          )}
        </ButtonBase>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right cluster: primary New-chat button + overflow menu.
            We swapped three near-invisible icon buttons for one clearly-
            labeled primary action and a Material-style "More" menu. This
            makes the chat-level CTAs (PDF / Save / New) genuinely usable
            instead of forcing users to hover and decode tiny glyphs. */}
        {plain && !showSwitcher ? null : plain ? (
          // Labelled, not a bare +: starting over is the action people look
          // for here, and it should read as one at a glance.
          <ToolbarButton icon={<AddRounded fontSize="small" />} onClick={handleNewChat} tooltip="Start a new chat">
            New chat
          </ToolbarButton>
        ) : (
        <Tooltip title={'Start a new chat (\u2318 N)'} placement="bottom">
          <Button
            size="small"
            variant="text"
            onClick={handleNewChat}
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            sx={{
              px: 1,
              py: 0.25,
              minHeight: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: 1.25,
              '&:hover': {
                color: 'text.primary',
                bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
              },
            }}
          >
            New chat
          </Button>
        </Tooltip>
        )}

        {threadTitle && (
          <>
            <Tooltip title="More actions" placement="bottom">
              <IconButton
                size="small"
                onClick={(e: React.MouseEvent<HTMLElement>) => setMoreAnchor(e.currentTarget)}
                sx={{ p: 0.5, ml: 0.25 }}
                aria-label="More actions"
              >
                <MoreVertRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={moreAnchor}
              open={Boolean(moreAnchor)}
              onClose={() => setMoreAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 200 } }}
            >
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  handleOpenSaveModal();
                }}
              >
                <ListItemIcon>
                  {isSaved
                    ? <BookmarkRounded fontSize="small" sx={{ color: 'primary.main' }} />
                    : <BookmarkBorderRounded fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={isSaved ? 'Saved to space' : 'Save to space'}
                  primaryTypographyProps={{ fontSize: '0.8125rem' }}
                />
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  setPlaybookModalOpen(true);
                }}
              >
                <ListItemIcon>
                  <MenuBookRounded fontSize="small" sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Save as Agent"
                  secondary="Rerun this analysis against fresh data"
                  primaryTypographyProps={{ fontSize: '0.8125rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Conversations: the kit's list, in a pop-over under the title. */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (t) => ({
              width: 360,
              maxWidth: `calc(100vw - ${t.spacing(4)})`,
              height: 'min(480px, 70vh)',
              mt: 0.5,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: t.tokens.surface.overlay,
              backgroundImage: 'none',
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            }),
          },
        }}
      >
        <ConversationList
          items={threads.map((t: CopilotThread) => ({
            id: t.threadId,
            title: t.title || null,
            kind: t.agentType === 'playbook' ? ('agent' as const) : ('chat' as const),
            date: t.createdAt,
          }))}
          activeId={currentThreadId}
          onSelect={(item) => handleSelectThread(item.id, item.title)}
          search={search}
          onSearchChange={setSearch}
          autoFocusSearch
          loading={threadsLoading}
          hasMore={hasMore}
          loadingMore={threadsLoadingMore}
          onShowMore={() => void loadThreads({ offset: threads.length, replace: false, q: query.trim() })}
        />
      </Popover>

      {/* Save to Space Modal */}
      <SaveToSpaceModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        spaces={spaces}
        loading={spacesLoading}
        error={spacesError}
        selectedSpaceId={selectedSpaceId}
        setSelectedSpaceId={setSelectedSpaceId}
        newSpaceName={newSpaceName}
        setNewSpaceName={setNewSpaceName}
        onSubmit={handleSaveToSpace}
        creating={isSaving}
        submitError={submitError}
      />

      {/* Save to Playbook Modal */}
      <SaveToPlaybookModal
        open={playbookModalOpen}
        onClose={() => setPlaybookModalOpen(false)}
        threadId={currentThreadId}
      />
    </>
  );
}
